import React from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/seo/SEO";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { ServiceSchema } from "@/components/seo/ServiceSchema";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, MapPin, Phone, Clock, Thermometer, Leaf, Euro } from "lucide-react";
import { track } from "@/lib/analytics";

const WarmtepompWestervoort = () => {
  const handleQuoteRequest = () => {
    track('quote_request', { service: 'warmtepomp', location: 'westervoort' });
    window.open('https://app.sol-vi.nl/form', '_blank');
  };

  const localBusinessData = {
    name: "SOL-VI Warmtepomp Westervoort",
    description: "Specialist in warmtepomp installatie in Westervoort. Erkend installateur voor duurzame verwarming.",
    url: "https://sol-vi.nl/warmtepomp-westervoort",
    telephone: "+31 6 55 33 18 70",
    email: "info@sol-vi.nl",
    address: {
      streetAddress: "Hoofdstraat 1",
      addressLocality: "Elst",
      addressRegion: "Gelderland",
      postalCode: "6661 EA",
      addressCountry: "NL"
    },
    openingHours: ["Mo-Fr 08:00-17:00"],
    priceRange: "€€€",
    serviceArea: ["Westervoort", "Elst", "Arnhem", "Duiven", "Gelderland"],
    aggregateRating: {
      ratingValue: 4.8,
      reviewCount: 73
    }
  };

  const serviceData = {
    name: "Warmtepomp Installatie Westervoort",
    description: "Professionele installatie van warmtepompen in Westervoort. Van hybride tot all-electric oplossingen.",
    provider: "SOL-VI",
    areaServed: ["Westervoort"],
    serviceType: "Warmtepomp Installatie",
    url: "https://sol-vi.nl/warmtepomp-westervoort",
    offers: [
      {
        name: "Hybride Warmtepomp",
        description: "Combinatie met bestaande cv-ketel voor optimale efficiency",
        priceRange: "€8.000 - €12.000"
      },
      {
        name: "All-Electric Warmtepomp",
        description: "Volledig elektrische verwarming zonder gas",
        priceRange: "€12.000 - €18.000"
      }
    ]
  };

  const breadcrumbData = [
    { name: "Home", url: "https://sol-vi.nl" },
    { name: "Warmtepompen", url: "https://sol-vi.nl/warmtepompen" },
    { name: "Westervoort", url: "https://sol-vi.nl/warmtepomp-westervoort" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Warmtepomp Westervoort | Hybride & All-Electric | SOL-VI"
        description="🏠 Warmtepomp installatie in Westervoort ⚡ Erkend installateur 💰 Tot €3.025 subsidie ✅ Gratis advies. Bel: 06-55331870"
        keywords="warmtepomp westervoort, warmtepomp installatie westervoort, hybride warmtepomp westervoort, all electric verwarming"
        canonical="https://sol-vi.nl/warmtepomp-westervoort"
        type="website"
      />
      
      <LocalBusinessSchema {...localBusinessData} />
      <ServiceSchema {...serviceData} />
      <BreadcrumbSchema items={breadcrumbData} />
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-primary">Westervoort, Gelderland</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Warmtepomp Westervoort
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  Duurzaam verwarmen in Westervoort met een warmtepomp. 
                  Tot 80% besparing op gasverbruik. Gratis advies en subsidie ondersteuning.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" onClick={handleQuoteRequest}>
                    Gratis Warmtepomp Advies
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+31616929110">
                        <Phone className="w-4 h-4 mr-2" />
                        Sven: 06-16929110
                      </a>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+31617105786">
                        <Phone className="w-4 h-4 mr-2" />
                        Nikki: 06-17105786
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Warmtepomp Voordelen</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Euro className="w-5 h-5 text-green-500" />
                    <span>Tot €3.025 ISDE subsidie</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-5 h-5 text-primary" />
                    <span>80% minder gasverbruik</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-600" />
                    <span>CO₂-neutraal verwarmen</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3D Westervoort Preview Section */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">
                Warmtepompen in Uw Buurt - Westervoort
              </h2>
              <p className="text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
                Bekijk Westervoort in 3D en ontdek waar wij warmtepompen hebben geïnstalleerd. 
                Visualiseer hoe een warmtepomp bij uw woning zou passen.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Interactieve 3D Weergave</h3>
                  <p className="text-muted-foreground mb-4">
                    Met de 3D viewer kunt u Westervoort verkennen en zien waar warmtepompen 
                    zijn geïnstalleerd. Dit geeft u een realistisch beeld van de mogelijkheden 
                    voor uw eigen woning.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Bekijk lokale installaties</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Verken verschillende woningtypes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Visualiseer mogelijkheden voor uw woning</span>
                    </div>
                  </div>
                </div>
                
                <Button 
                  size="lg" 
                  onClick={() => window.open('https://3dbag.nl/nl/viewer?rdx=195028.78924649316&rdy=441334.61316546483&ox=310.7697840287874&oy=562.988852515329&oz=489.3314637727599', '_blank')}
                  className="w-full sm:w-auto"
                >
                  🏠 Bekijk Westervoort in 3D
                </Button>
              </div>
              
              <div className="relative">
                <div className="aspect-video rounded-lg overflow-hidden shadow-lg border">
                  <iframe
                    src="https://3dbag.nl/nl/viewer?rdx=195028.78924649316&rdy=441334.61316546483&ox=310.7697840287874&oy=562.988852515329&oz=489.3314637727599"
                    width="100%"
                    height="100%"
                    title="3D weergave Westervoort - Warmtepomp locaties"
                    className="border-0"
                    loading="lazy"
                    allowFullScreen
                  />
                </div>
                <div className="absolute top-2 right-2 bg-background/90 px-2 py-1 rounded text-xs text-muted-foreground">
                  Interactieve 3D viewer
                </div>
              </div>
            </div>
            
            <div className="mt-8 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground text-center">
                💡 <strong>Tip:</strong> Gebruik de 3D viewer om uw eigen woning te vinden en te zien 
                hoe andere woningen in Westervoort zijn uitgerust met warmtepompen. 
                Klik en sleep om te navigeren, scroll om in te zoomen.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Waarom een Warmtepomp in Westervoort?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Energiebesparing</CardTitle>
                  <CardDescription>
                    Tot 80% minder gasverbruik door hoge efficiency
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Een warmtepomp haalt energie uit de buitenlucht en zet dit om in warmte voor uw huis.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Financiële Voordelen</CardTitle>
                  <CardDescription>
                    ISDE subsidie tot €3.025 en lagere energiekosten
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Profiteer van overheidssubsidie en bespaar structureel op uw energierekening.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Comfort & Duurzaamheid</CardTitle>
                  <CardDescription>
                    Stabiele temperatuur en milieuvriendelijk verwarmen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Constant comfort zonder temperatuurschommelingen en bijdrage aan een groenere toekomst.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Types Section */}
        <section className="py-16 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Warmtepomp Oplossingen voor Westervoort
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Hybride Warmtepomp</CardTitle>
                  <CardDescription>Ideaal voor bestaande woningen</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Combineert uw bestaande cv-ketel met een warmtepomp voor optimale efficiency.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Behoudt bestaande cv-ketel</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Lagere investering</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">50-70% gasbesparing</span>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-primary">Vanaf €8.000</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>All-Electric Warmtepomp</CardTitle>
                  <CardDescription>Volledig gasvrij verwarmen</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Vervangt uw cv-ketel volledig voor 100% elektrische verwarming.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Volledig gasvrij</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">Maximale duurzaamheid</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">80%+ energiebesparing</span>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-primary">Vanaf €12.000</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Local Projects */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Warmtepompen in Westervoort
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Centrum Westervoort</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Hybride warmtepomp, rijwoning uit 1985
                  </p>
                  <p className="text-sm">
                    "Installatie verliep soepel, merkbaar lagere gasrekening en huis wordt 
                    sneller warm!" - Familie Visser
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Westervoort-Noord</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    All-electric warmtepomp, nieuwbouwwoning
                  </p>
                  <p className="text-sm">
                    "Helemaal gasvrij en constant comfort. Precies wat we zochten voor 
                    onze nieuwe woning." - Dhr. Peters
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Westervoort-Zuid</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Hybride warmtepomp met zonnepanelen
                  </p>
                  <p className="text-sm">
                    "Perfecte combinatie! De warmtepomp draait grotendeels op zonnestroom." 
                    - Mevr. de Jong
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-subtle">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Klaar voor een Warmtepomp in Westervoort?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Ontdek of uw woning geschikt is voor een warmtepomp. 
              Gratis advies en ondersteuning bij subsidieaanvraag.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={handleQuoteRequest}>
                Gratis Geschiktheidscheck
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/contact">Contact Opnemen</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default WarmtepompWestervoort;